/**
 * progress.ts — durable per-picture progress (the meta loop the gallery remembers).
 *
 * Persists "how far you got" + "finished" per pictureId in localStorage so the gallery can show
 * progress %, a Done state, and a "Continue painting" surface — and so finishing a picture feels
 * permanent. Cosmetic only (no money/EV); the server stays authoritative for everything monetary.
 */
const KEY = 'colordrop.progress.v1';

export interface PicProgress {
  progress01: number; // 0..1 cells painted
  completed: boolean; // finished at least once
  lastOpened: number; // epoch ms (for "continue" ordering)
}

type Store = Record<string, PicProgress>;

function load(): Store {
  try {
    if (typeof localStorage === 'undefined') return {};
    return JSON.parse(localStorage.getItem(KEY) ?? '{}') as Store;
  } catch {
    return {};
  }
}

function save(s: Store): void {
  try {
    if (typeof localStorage !== 'undefined') localStorage.setItem(KEY, JSON.stringify(s));
  } catch {
    /* private mode / quota — progress just won't persist */
  }
}

export const Progress = {
  get(id: string): PicProgress | undefined {
    return load()[id];
  },
  all(): Store {
    return load();
  },
  /** Merge a patch for a picture (defaults fill in for first write). */
  update(id: string, patch: Partial<PicProgress>): void {
    const s = load();
    const prev = s[id] ?? { progress01: 0, completed: false, lastOpened: 0 };
    s[id] = { ...prev, ...patch };
    save(s);
  },
  /** Count of finished pictures (for the meta header). */
  completedCount(): number {
    const s = load();
    return Object.values(s).filter((p) => p.completed).length;
  },
};
